/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { DeletedNodeEntry } from '../model/deletedNodeEntry';
import { DeletedNodesPaging } from '../model/deletedNodesPaging';
import { DirectAccessUrlEntry } from '../model/directAccessUrlEntry';
import { NodeEntry } from '../model/nodeEntry';
import { RenditionEntry } from '../model/renditionEntry';
import { RenditionPaging } from '../model/renditionPaging';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
import { buildCollectionParam } from '../../../alfrescoApiClient';
export class TrashcanApi extends BaseApi {
    deleteDeletedNode(nodeId) {
        throwIfNotDefined(nodeId, 'nodeId');
        const pathParams = {
            nodeId
        };
        return this.delete({
            path: '/deleted-nodes/{nodeId}',
            pathParams
        });
    }
    getArchivedNodeRendition(nodeId, renditionId) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(renditionId, 'renditionId');
        const pathParams = {
            nodeId,
            renditionId
        };
        return this.get({
            path: '/deleted-nodes/{nodeId}/renditions/{renditionId}',
            pathParams,
            returnType: RenditionEntry
        });
    }
    getArchivedNodeRenditionContent(nodeId, renditionId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(renditionId, 'renditionId');
        opts = opts || {};
        const pathParams = {
            nodeId,
            renditionId
        };
        const queryParams = {
            attachment: opts === null || opts === void 0 ? void 0 : opts.attachment,
            placeholder: opts === null || opts === void 0 ? void 0 : opts.placeholder
        };
        const headerParams = {
            'If-Modified-Since': opts === null || opts === void 0 ? void 0 : opts.ifModifiedSince,
            Range: opts === null || opts === void 0 ? void 0 : opts.range
        };
        return this.get({
            path: '/deleted-nodes/{nodeId}/renditions/{renditionId}/content',
            pathParams,
            queryParams,
            headerParams,
            accepts: ['application/octet-stream']
        });
    }
    getDeletedNode(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv')
        };
        return this.get({
            path: '/deleted-nodes/{nodeId}',
            pathParams,
            queryParams,
            returnType: DeletedNodeEntry
        });
    }
    getDeletedNodeContent(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            attachment: opts === null || opts === void 0 ? void 0 : opts.attachment
        };
        const headerParams = {
            'If-Modified-Since': opts === null || opts === void 0 ? void 0 : opts.ifModifiedSince,
            Range: opts === null || opts === void 0 ? void 0 : opts.range
        };
        const accepts = ['application/octet-stream'];
        return this.get({
            path: '/deleted-nodes/{nodeId}/content',
            pathParams,
            queryParams,
            headerParams,
            accepts,
            returnType: 'blob'
        });
    }
    listDeletedNodeRenditions(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            where: opts === null || opts === void 0 ? void 0 : opts.where
        };
        return this.get({
            path: '/deleted-nodes/{nodeId}/renditions',
            pathParams,
            queryParams,
            returnType: RenditionPaging
        });
    }
    listDeletedNodes(opts) {
        const queryParams = Object.assign(Object.assign({}, (opts || {})), { include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv') });
        return this.get({
            path: '/deleted-nodes',
            queryParams,
            returnType: DeletedNodesPaging
        });
    }
    restoreDeletedNode(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        const pathParams = {
            nodeId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.post({
            path: '/deleted-nodes/{nodeId}/restore',
            pathParams,
            queryParams,
            bodyParam: opts === null || opts === void 0 ? void 0 : opts.deletedNodeBodyRestore,
            returnType: NodeEntry
        });
    }
    requestDirectAccessUrl(nodeId) {
        throwIfNotDefined(nodeId, 'nodeId');
        const pathParams = {
            nodeId
        };
        return this.post({
            path: '/deleted-nodes/{nodeId}/request-direct-access-url',
            pathParams,
            returnType: DirectAccessUrlEntry
        });
    }
    requestRenditionDirectAccessUrl(nodeId, renditionId) {
        throwIfNotDefined(nodeId, 'nodeId');
        throwIfNotDefined(renditionId, 'renditionId');
        const pathParams = {
            nodeId,
            renditionId
        };
        return this.post({
            path: '/deleted-nodes/{nodeId}/renditions/{renditionId}/request-direct-access-url',
            pathParams,
            returnType: DirectAccessUrlEntry
        });
    }
}
//# sourceMappingURL=../../../../../../../../lib/js-api/src/api/content-rest-api/api/trashcan.api.js.map